//import { useSubscription } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import React, { ReactNode, useState } from 'react';

//import { useTranslation } from 'react-i18next';
//import { useAppContext } from '../hooks/AppContext';
import {
  RoomsDocument,
  RoomsForVehicleDocument,
  RoomsForVehicleQuery,
  RoomsQuery,
  RoomType,
  //useMarkRoomAsReadMutation,
  // useRoomsForVehicleLazyQuery,
} from '../types/api.graphql';
//import { MessagesSubscriptionGQL } from '../types/subscriptions';
import { getImageFromChatMessage, getTextFromChatMessage } from '../utils/messages';
import { useAppContainerContext } from './AppContainerContext';
//import { useSessionContext } from './SessionContext';

type ChatListProviderProps = {
  children: ReactNode;
};

interface ChatListProviderValuesTypes {
  vehicleChats: any;
  setVehicleChats: (value: any) => void;
  generalChats: any;
  setGeneralChats: (value: any) => void;
  unreadGeneralCount: number;
  unreadTotalCount: number;
  activeRoomData: any;
  setActiveRoomData: (value: any) => void;
  resetActiveRoomCount: () => void;
}

export const ChatListContext = React.createContext<ChatListProviderValuesTypes>(undefined as any);

export const ChatListProvider = ({ children }: ChatListProviderProps) => {
  // Dependencies
  //const { me } = useSessionContext();
  //const { showAlert } = useAppContext();
  const { actualVDPVehicle } = useAppContainerContext();
  //const { t } = useTranslation();
  const { cache } = useApolloClient();
  //const [markRoomAsReadMutation] = useMarkRoomAsReadMutation();
  // const [getVehicleRooms] = useRoomsForVehicleLazyQuery();

  // State
  const [vehicleChats, setVehicleChats] = useState<any>();
  const [generalChats, setGeneralChats] = useState<any>();
  const [unreadGeneralCount, setUnreadGeneralCount] = useState(0);
  const [activeRoomData, setActiveRoomData] = useState<any | undefined>(undefined);

  // Derived State
  const unreadTotalCount = unreadGeneralCount;
  /*
  // Helpers
  const markRoomAsRead = (roomId: string) => {
    void (async () => {
      await markRoomAsReadMutation({ variables: { input: { roomId } } });
    })();
  };*/

  const parseAndSortRooms = (rooms: any[]) => {
    const sortedNodes = rooms?.slice().sort(function (nodeA, nodeB) {
      const aDate: number = new Date(nodeA?.updatedAt)?.getTime();
      const bDate: number = new Date(nodeB?.updatedAt)?.getTime();
      const order = bDate - aDate;
      return order;
    });

    return sortedNodes?.map((room) => {
      return {
        img: room?.imageUrl,
        name: room?.name,
        messageImg: getImageFromChatMessage(room?.lastMessageText),
        text: getTextFromChatMessage(room?.lastMessageText),
        id: room?.id,
        group: room?.type !== RoomType.InOrgDirectRoom,
        date: room.updatedAt,
        organization: room.ownerOrganization,
        type: room?.type,
        users: room?.users,
        count: room?.unreadMessagesCount,
        vehicle: room?.vehicle,
      };
    });
  };

  const processRoomsData = (data: RoomsQuery) => {
    setUnreadGeneralCount(
      data?.rooms?.nodes?.reduce((partial, room) => {
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        return partial + (room.unreadMessagesCount || 0);
      }, 0),
    );

    return parseAndSortRooms(data?.rooms?.nodes);
  };

  const processVehicleRoomsData = (data: RoomsForVehicleQuery) => {
    if (data?.roomsForVehicle.rooms.length > 0) {
      return parseAndSortRooms(data?.roomsForVehicle.rooms);
    } else {
      return [
        {
          auxiliar: true,
          name: actualVDPVehicle?.organization?.name,
          type: RoomType.ListingInternalRoom,
          group: true,
          img: actualVDPVehicle?.organization?.imageUrl,
          vehicle: {
            id: actualVDPVehicle?.id,
            displayName: actualVDPVehicle?.displayName,
            pictures: actualVDPVehicle?.pictures,
          },
          organization: {
            id: actualVDPVehicle?.organization?.id,
            name: actualVDPVehicle?.organization?.name,
          },
        },
      ];
    }
  };

  // This resets the count for a room when user opens the conversation
  const resetActiveRoomCount = () => {
    // Logic for general room
    const cachedData: any = cache.readQuery({
      query: RoomsDocument,
      variables: {
        first: 20,
      },
    });

    const rooms = cachedData?.rooms?.nodes;
    const auxRooms = [...rooms];
    // Get the conversation room
    const existingRoom = auxRooms?.find((node) => node.id === activeRoomData?.id);
    const auxExistingRoom = { ...existingRoom };
    // Reset count for room
    auxExistingRoom.unreadMessagesCount = 0;

    const updatedRooms = auxRooms.map((room) => {
      if (room.id === auxExistingRoom.id) {
        return auxExistingRoom;
      } else {
        return room;
      }
    });

    const newRoomsData = {
      ...cachedData.rooms,
      nodes: updatedRooms,
    };
    cache.writeQuery({
      query: RoomsDocument,
      variables: {
        first: 20,
      },
      data: {
        rooms: newRoomsData,
      },
    });
    setGeneralChats(processRoomsData({ rooms: newRoomsData }));

    // TODO: Add logic to reset count in vehicleRooms
    if (activeRoomData.vehicle?.id && activeRoomData.vehicle?.id === actualVDPVehicle?.id) {
      const vehicleRoomsCachedData: any = cache.readQuery({
        query: RoomsForVehicleDocument,
        variables: {
          vehicleId: activeRoomData.vehicle?.id,
        },
      });

      const rooms = vehicleRoomsCachedData?.roomsForVehicle?.rooms;
      const auxRooms = [...rooms];
      // Get the conversation room
      const existingRoom = auxRooms?.find((room) => room.id === activeRoomData?.id);
      const auxExistingRoom = { ...existingRoom };
      // Reset count for room
      auxExistingRoom.unreadMessagesCount = 0;

      const updatedRooms = auxRooms.map((room) => {
        if (room.id === auxExistingRoom.id) {
          return auxExistingRoom;
        } else {
          return room;
        }
      });

      const newRoomsData = {
        ...vehicleRoomsCachedData.roomsForVehicle,
        rooms: updatedRooms,
      };

      cache.writeQuery({
        query: RoomsForVehicleDocument,
        variables: {
          vehicleId: activeRoomData.vehicle?.id,
        },
        data: {
          roomsForVehicle: newRoomsData,
        },
      });

      setVehicleChats(processVehicleRoomsData({ roomsForVehicle: newRoomsData }));
    }
  };

  // TODO: General chat will remain hidden for now.
  // const getVehicleRoomsById = async (vehicleId: string) => {
  //   const response = await getVehicleRooms({
  //     variables: { vehicleId },
  //   });
  //   if (response?.data) {
  //     setVehicleChats(processVehicleRoomsData(response?.data));
  //   }
  // };

  // Queries
  // TODO: General chat will remain hidden for now.
  // useRoomsQuery({
  //   variables: {
  //     first: 20,
  //   },
  //   fetchPolicy: 'cache-and-network',
  //   skip: !!generalChats,
  //   onCompleted: (data: RoomsQuery) => {
  //     setGeneralChats(processRoomsData(data));
  //   },
  // });

  /*
  TODO: This subscription breaks the cache and should be corrected 
  useSubscription(MessagesSubscriptionGQL, {
    onData: ({ data: { data } }) => {
      //setNewSubscriptionMessage(data);
      const newMessage = data?.messageCreated;

      const loggedUserIsSender = me.id === newMessage.sender?.id;

      const messageRoom = { ...newMessage?.room };
      const messageForActualActiveRoom = messageRoom.id === activeRoomData?.id;
      // If the message is for the already active conversation need to automatically mark messages as read.
      if (messageForActualActiveRoom) {
        markRoomAsRead(messageRoom.id);
      }

      const cachedData: any = cache.readQuery({
        query: RoomsDocument,
        variables: {
          first: 20,
        },
      });

      const existingRoom = cachedData?.rooms?.nodes?.find((node) => node.id === messageRoom?.id);
      if (existingRoom) {
        if (messageForActualActiveRoom) {
          // conversation is open so i need to modify the room automatically updated by apollo with unread count = 0
          const auxRooms = [...cachedData.rooms.nodes];
          // update the count to 0
          messageRoom.unreadMessagesCount = 0;
          const updatedRooms = auxRooms.map((room) => {
            if (room.id === messageRoom.id) {
              return messageRoom;
            } else {
              return room;
            }
          });

          const newRoomsData = {
            ...cachedData.rooms,
            nodes: updatedRooms,
          };

          cache.writeQuery({
            query: RoomsDocument,
            variables: {
              first: 20,
            },
            data: {
              rooms: newRoomsData,
            },
          });
          setGeneralChats(processRoomsData({ rooms: newRoomsData }));
        } else {
          // As the room node already existed and is not open so Apollo already updated it for us
          setGeneralChats(processRoomsData(cachedData));
        }
      } else {
        // It's a new room i will need to add it manually
        const auxRooms = [...(cachedData?.rooms?.nodes ?? [])];
        auxRooms.push(messageRoom);
        const newRoomsData = {
          ...data.rooms,
          nodes: auxRooms,
        };
        // write the new node in rooms query cache
        cache.writeQuery({
          query: RoomsDocument,
          variables: {
            first: 20,
          },
          data: {
            rooms: newRoomsData,
          },
        });
        // update the state with the new data
        setGeneralChats(processRoomsData({ rooms: newRoomsData }));
      }
      const vehicleRoomsCachedData: any = cache.readQuery({
        query: RoomsForVehicleDocument,
        variables: {
          vehicleId: messageRoom.vehicle?.id,
        },
      });

      if (actualVDPVehicle?.id === messageRoom.vehicle?.id && vehicleRoomsCachedData) {
        const existingRoom = vehicleRoomsCachedData?.roomsForVehicle.rooms.find((room) => {
          return room.id === messageRoom.id;
        });

        if (existingRoom) {
          if (messageForActualActiveRoom) {
            // conversation is open so i need to modify the room automatically updated by apollo with unread count = 0
            const auxRooms = [...vehicleRoomsCachedData.roomsForVehicle.rooms];
            // update the count to 0
            messageRoom.unreadMessagesCount = 0;
            const updatedRooms = auxRooms.map((room) => {
              if (room.id === messageRoom.id) {
                return messageRoom;
              } else {
                return room;
              }
            });

            const newRoomsForVehicleData = {
              ...vehicleRoomsCachedData.roomsForVehicle,
              rooms: updatedRooms,
            };

            cache.writeQuery({
              query: RoomsForVehicleDocument,
              variables: {
                vehicleId: messageRoom.vehicle?.id,
              },
              data: {
                roomsForVehicle: newRoomsForVehicleData,
              },
            });
            setVehicleChats(processVehicleRoomsData({ roomsForVehicle: newRoomsForVehicleData }));
          } else {
            //As the room node already existed and is not open so Apollo already updated it for us
            setVehicleChats(processVehicleRoomsData(vehicleRoomsCachedData));
          }
        } else {
          // It's a new room i will need to add it manually
          const auxRooms = [...vehicleRoomsCachedData.roomsForVehicle.rooms];
          auxRooms.push(messageRoom);
          const newRoomsForVehicleData = {
            ...vehicleRoomsCachedData.roomsForVehicle,
            rooms: auxRooms,
          };
          // write the new node in rooms query cache
          cache.writeQuery({
            query: RoomsForVehicleDocument,
            variables: {
              vehicleId: messageRoom.vehicle?.id,
            },
            data: {
              roomsForVehicle: newRoomsForVehicleData,
            },
          });
          // update the state with the new data
          setVehicleChats(processVehicleRoomsData({ roomsForVehicle: newRoomsForVehicleData }));
        }
      }
      if (!loggedUserIsSender && !messageForActualActiveRoom) {
        const senderName = [newMessage.sender.firstName, newMessage.sender.lastName].join(' ');
        const vehicleMessage = messageRoom.vehicle;
        const vehicleName =
          vehicleMessage?.displayName || [vehicleMessage?.year, vehicleMessage?.make, vehicleMessage?.model].join(' ');
        const orgInviteSender =
          newMessage.sender?.organizations?.[0]?.id === messageRoom.invitedOrganization?.id ? 'vendor' : 'owner';

        switch (messageRoom.type) {
          case RoomType.InOrgDirectRoom:
            showAlert(t('components.chat.directRoomNewMsg', { senderName: senderName }));
            break;
          case RoomType.InOrgGeneralRoom:
            showAlert(t('components.chat.generalRoomNewMsg', { senderName: senderName }));
            break;
          case RoomType.ListingInternalRoom:
            showAlert(t('components.chat.vehicleIntRoomNewMsg', { senderName: senderName, vehicleName: vehicleName }));
            break;
          case RoomType.OrgInviteRoom:
            showAlert(
              t('components.chat.invOrgRoomNewMsg', {
                senderName: senderName,
                role: orgInviteSender,
                vehicleName: vehicleName,
              }),
            );
            break;
        }
      }
    },
  });*/

  // Effects

  // TODO: General chat will remain hidden for now.
  // React.useEffect((): any => {
  //   if (actualVDPVehicle) {
  //     void (async () => {
  //       await getVehicleRoomsById(actualVDPVehicle?.id);
  //     })();
  //     /**
  //      TODO: Add new logic when navigating to vehicle detail
  //      */
  //   } else {
  //     setVehicleChats(undefined);
  //   }
  // }, [actualVDPVehicle]);

  return (
    <ChatListContext.Provider
      value={{
        vehicleChats,
        generalChats,
        unreadGeneralCount,
        unreadTotalCount,
        setVehicleChats,
        setGeneralChats,
        activeRoomData,
        setActiveRoomData,
        resetActiveRoomCount,
      }}
    >
      {children}
    </ChatListContext.Provider>
  );
};

export function useChatListContext() {
  return React.useContext(ChatListContext);
}
